import posthog from 'posthog-js/dist/module.full.no-external'

/**
 * PostHog reserves `$feature/<key>` for the variant a person was assigned. Its
 * experiment queries read that property off every event, which is what lets an
 * experiment run "without feature flags": we assign the variant ourselves and
 * report it here.
 */
export function experimentFeatureProperty(experimentKey: string) {
  return `$feature/${experimentKey}`
}

const activeVariants = new Map<string, string>()

function register(experimentKey: string, variant: string) {
  try {
    // `register` overwrites the key rather than merging, so a variant persisted
    // in localStorage by an earlier assignment can never outlive a new one.
    posthog.register({ [experimentFeatureProperty(experimentKey)]: variant })
    return true
  } catch {
    return false
  }
}

/**
 * Registers the variant as a persistent super property and remembers it, so it
 * survives the `posthog.reset()` a wallet disconnect or account switch
 * triggers. Returns false when PostHog refused the registration.
 *
 * This module deliberately imports nothing but PostHog: `consent.ts` calls it,
 * and the app's env-validating logger would break the component tests that
 * mount the consent provider.
 */
export function registerExperimentVariant(experimentKey: string, variant: string) {
  if (!register(experimentKey, variant)) return false

  activeVariants.set(experimentKey, variant)
  return true
}

/** Drops the super property and stops re-registering it after a reset. */
export function unregisterExperimentVariant(experimentKey: string) {
  activeVariants.delete(experimentKey)

  try {
    posthog.unregister(experimentFeatureProperty(experimentKey))
    return true
  } catch {
    return false
  }
}

/**
 * Re-registers every variant registered so far. `posthog.reset()` wipes super
 * properties, so this runs from `registerSessionProperties()` after every
 * reset. Without it a disconnect or account switch drops variant attribution
 * until the exposing component happens to remount.
 *
 * Callers must not run it while the user is opted out: `register` persists to
 * localStorage even when capturing is off.
 */
export function registerActiveExperiments() {
  for (const [experimentKey, variant] of activeVariants) register(experimentKey, variant)
}

/** Every experiment registered so far, as (key, variant) pairs. */
export function getActiveExperiments() {
  return [...activeVariants]
}
